import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, TextArea, EmptyState, Modal, Alert, Label, Spinner } from "@/components/ui";
import { isoLocalDate } from "@/lib/helpers";
import { Plus, ChevronLeft, ChevronRight, Stethoscope, User, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";
import { useEventForm } from "@/hooks/useEventForm";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function Calendar() {
  const [events, setEvents] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [signOff, setSignOff] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(() => new Date());

  const load = useCallback(async () => setEvents(await api.listEvents()), []);
  useEffect(() => { load(); }, [load]);

  const eventDates = useMemo(() => new Set((events || []).map((e) => {
    try { return isoLocalDate(new Date(e.starts_at)); } catch { return null; }
  }).filter(Boolean)), [events]);

  const dayISO = isoLocalDate(selectedDay);
  const dayEvents = useMemo(() => (events || []).filter((e) => {
    try { return isoLocalDate(new Date(e.starts_at)) === dayISO; } catch { return false; }
  }).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)), [events, dayISO]);

  if (!events) return <Spinner />;

  const del = async (e) => {
    const scope = e.series_id
      ? (window.confirm(`"${e.title}" is recurring. OK = delete the ENTIRE series, Cancel = only this occurrence.`) ? "all" : "this")
      : "this";
    if (!e.series_id && !window.confirm(`Delete "${e.title}"?`)) return;
    try { await api.deleteEvent(e.id, scope); await load(); toast.success("Event deleted"); }
    catch (ex) { toast.error(ex?.detail || "Delete failed"); }
  };

  // build calendar grid
  const y = viewMonth.getFullYear(), mo = viewMonth.getMonth();
  const first = new Date(y, mo, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, mo, d));

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Calendar" title="Appointments & shifts"
        action={<Button variant="accent" data-testid="add-event-button" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add</Button>} />

      <MonthGrid viewMonth={viewMonth} cells={cells} dayISO={dayISO} eventDates={eventDates}
        onPrev={() => setViewMonth(new Date(y, mo - 1, 1))}
        onNext={() => setViewMonth(new Date(y, mo + 1, 1))}
        onSelectDay={setSelectedDay} />

      <div className="flex items-end justify-between mt-6 mb-3">
        <Label className="text-ink">{selectedDay.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</Label>
        <span className="text-xs text-subtle" data-testid="day-event-count">{dayEvents.length} event{dayEvents.length === 1 ? "" : "s"}</span>
      </div>

      {dayEvents.length === 0 ? (
        <Card><EmptyState testid="calendar-empty" icon={Stethoscope} title="Nothing on this day"
          body="Add an appointment or aide shift for this date."
          action={<Button data-testid="calendar-empty-add-cta" onClick={() => setAddOpen(true)}>Add for this day</Button>} /></Card>
      ) : (
        <div className="space-y-3">
          {dayEvents.map((e) => (
            <DayEventCard key={e.id} e={e} onSignOff={() => setSignOff(e)} onDelete={() => del(e)} />
          ))}
        </div>
      )}

      <AddEventModal open={addOpen} onClose={() => setAddOpen(false)} preset={selectedDay}
        onSaved={async () => { setAddOpen(false); await load(); toast.success("Event added"); }} />
      <SignOffModal event={signOff} onClose={() => setSignOff(null)}
        onSaved={async () => { setSignOff(null); await load(); toast.success("Shift signed off"); }} />
    </div>
  );
}

function MonthGrid({ viewMonth, cells, dayISO, eventDates, onPrev, onNext, onSelectDay }) {
  const todayISO = isoLocalDate(new Date());
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <button data-testid="cal-prev" onClick={onPrev} className="p-1.5 rounded-lg hover:bg-brand-tint"><ChevronLeft className="w-5 h-5 text-brand-dark" /></button>
        <div className="font-display text-lg text-ink">{viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
        <button data-testid="cal-next" onClick={onNext} className="p-1.5 rounded-lg hover:bg-brand-tint"><ChevronRight className="w-5 h-5 text-brand-dark" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs text-subtle font-semibold mb-1">
        {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`pad-${i}`} />;
          const iso = isoLocalDate(d);
          const isSel = iso === dayISO;
          const isToday = iso === todayISO;
          const hasEvent = eventDates.has(iso);
          return (
            <button key={iso} data-testid={`cal-day-${iso}`} onClick={() => onSelectDay(d)}
              className={`relative aspect-square rounded-lg text-sm flex items-center justify-center transition-colors
                ${isSel ? "bg-brand text-white font-semibold" : isToday ? "bg-brand-tint text-brand-dark font-semibold" : "hover:bg-brand-tint text-ink"}`}>
              {d.getDate()}
              {hasEvent && <span className={`absolute bottom-1 w-1.5 h-1.5 rounded-full ${isSel ? "bg-mint" : "bg-mint-dark"}`} />}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function DayEventCard({ e, onSignOff, onDelete }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center shrink-0">
          {e.kind === "aide_shift" ? <User className="w-5 h-5 text-brand-dark" /> : <Stethoscope className="w-5 h-5 text-brand-dark" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-ink truncate">{e.title}</span>
            {e.series_id && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-dark bg-brand-tint border border-line rounded-full px-1.5 py-0.5"><Repeat className="w-3 h-3" />Recurring</span>}
          </div>
          <div className="text-sm text-subtle">{new Date(e.starts_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}{e.notes ? ` · ${e.notes}` : ""}</div>
        </div>
        {e.kind === "aide_shift" && (
          e.signed_off
            ? <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-ok text-white">Signed</span>
            : <button data-testid={`signoff-${e.id}`} onClick={onSignOff} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-brand-tint text-brand-dark border border-line hover:bg-white">Sign off</button>
        )}
        <button data-testid={`event-delete-${e.id}`} onClick={onDelete} className="p-1.5 text-subtle hover:text-danger"><Trash2 className="w-4 h-4" /></button>
      </div>
      {e.signed_off && e.signoff_summary && (
        <div className="mt-3 p-3 bg-brand-tint rounded-lg">
          <div className="text-xs font-semibold text-subtle mb-1">Shift note by {e.signed_off_by}</div>
          <div className="text-sm text-ink">{e.signoff_summary}</div>
        </div>
      )}
    </Card>
  );
}

function AddEventModal({ open, onClose, onSaved, preset }) {
  const f = useEventForm({ open, preset, onSaved });
  const chip = (active) => `px-3.5 py-2 rounded-full text-xs font-semibold border ${active ? "bg-brand text-white border-brand" : "bg-brand-tint text-brand-dark border-line"}`;

  return (
    <Modal open={open} onClose={onClose} title="New event" testid="add-event-modal">
      <div className="flex gap-2 mb-4">
        {["appointment", "aide_shift"].map((k) => (
          <button key={k} data-testid={`kind-${k}`} onClick={() => f.setKind(k)} className={chip(f.kind === k)}>
            {k === "aide_shift" ? "Aide shift" : "Appointment"}
          </button>
        ))}
      </div>
      <Field label="Title" data-testid="event-title-input" value={f.title} onChange={(e) => f.setTitle(e.target.value)} placeholder="Cardiology check-up" />
      <Field label="When (YYYY-MM-DD HH:mm)" data-testid="event-when-input" value={f.when} onChange={(e) => f.setWhen(e.target.value)} placeholder="2026-06-20 10:30" />
      <Field label="Notes (optional)" data-testid="event-notes-input" value={f.notes} onChange={(e) => f.setNotes(e.target.value)} placeholder="Bring meds list" />

      <div className="border border-line rounded-lg p-3 mb-4 bg-white">
        <Label>Reminders</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {[[10, "10 min"], [30, "30 min"], [60, "1 hour"], [1440, "1 day"], [2880, "2 days"]].map(([m, lbl]) => (
            <button key={m} data-testid={`reminder-chip-${m}`} onClick={() => f.toggleRem(m)} className={chip(f.reminders.includes(m))}>{lbl} before</button>
          ))}
        </div>
      </div>

      <div className="border border-line rounded-lg p-3 mb-4 bg-white">
        <div className="flex items-center justify-between">
          <Label>Repeats</Label>
          <button data-testid="recurrence-toggle" onClick={() => f.setRecurring((v) => !v)}
            className={`w-11 h-6 rounded-full p-0.5 transition-colors ${f.recurring ? "bg-brand" : "bg-line"}`}>
            <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${f.recurring ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {f.recurring && (
          <>
            <div className="flex gap-2 mt-3">
              {["daily", "weekly", "monthly"].map((fr) => (
                <button key={fr} data-testid={`freq-${fr}`} onClick={() => f.setFreq(fr)} className={`${chip(f.freq === fr)} capitalize`}>{fr}</button>
              ))}
            </div>
            <div className="flex gap-3 mt-3">
              <Field label="Every" data-testid="recurrence-interval" value={f.interval} onChange={(e) => f.setIntervalV(e.target.value)} className="w-full" />
              <Field label="Occurrences" data-testid="recurrence-count" value={f.count} onChange={(e) => f.setCount(e.target.value)} className="w-full" />
            </div>
          </>
        )}
      </div>

      <Alert testid="add-event-error">{f.err}</Alert>
      <Button data-testid="save-event-button" onClick={f.save} loading={f.busy} disabled={!f.canSave} className="w-full">Save event</Button>
    </Modal>
  );
}

function SignOffModal({ event, onClose, onSaved }) {
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.signoffEvent(event.id, summary.trim() || "Shift completed"); setSummary(""); onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={!!event} onClose={onClose} title="Sign off shift" testid="signoff-modal">
      <p className="text-sm text-subtle mb-3">{event?.title} · {event ? new Date(event.starts_at).toLocaleString() : ""}</p>
      <TextArea label="Shift summary" data-testid="signoff-summary-input" value={summary} onChange={(e) => setSummary(e.target.value)}
        placeholder="Bathed, took meds, walked 20 min. Ate well." rows={4} />
      <Button data-testid="save-signoff-button" onClick={save} loading={busy} className="w-full">Sign off</Button>
    </Modal>
  );
}
